// What the chat window shows with an AI Assistant answer besides its text
// (ChatbotWidget): the follow-up questions the assistant suggests - the
// ```followups block of its Markdown - and, from the data tools it called
// (tools_used, or a transcript's tools: { name, arguments }) and the meters
// their data named (meter_ids), the answer's Source line and its buttons. A
// root cause analysis (Investigation mode) also comes with what heads it -
// investigation: { issue, title, period, sources, alert_type } - which its
// Source line and buttons are made from instead.
import { PAGES } from "../mw2/context";

const FOLLOW_UPS = /^\s{0,3}(`{3,}|~{3,})\s*(?:follow[-_ ]?ups?|suggestions)\s*$/i;
const MAX_FOLLOW_UPS = 3;

// A fence closes on a line of its own character, at least as long.
function closes(line, fence) {
  const text = line.trim();
  return text.length >= fence.length && [...text].every((char) => char === fence[0]);
}

// A suggested question as asked: no list marker, bold or quotes around it.
const asQuestion = (line) =>
  line
    .trim()
    .replace(/^(?:[-*+•]|\d{1,2}[.)])\s+/, "")
    .replace(/\*\*|__/g, "")
    .replace(/^["'“‘]+|["'”’]+$/g, "")
    .trim()
    .slice(0, 160);

/** The answer's text without its ```followups block, and the questions in it. */
export function splitFollowUps(text) {
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");
  const body = [];
  const followUps = [];
  for (let i = 0; i < lines.length; i += 1) {
    const open = lines[i].match(FOLLOW_UPS);
    if (!open) {
      body.push(lines[i]);
      continue;
    }
    for (i += 1; i < lines.length && !closes(lines[i], open[1]); i += 1) {
      const question = asQuestion(lines[i]);
      if (question && !followUps.includes(question)) followUps.push(question);
    }
  }
  return { body: body.join("\n").trimEnd(), followUps: followUps.slice(0, MAX_FOLLOW_UPS) };
}

// What each tool reads, as the Source line names it.
const SOURCES = {
  getWaterLeak: "leak meters",
  getMeterHealth: "meter health",
  getAnomalyMeters: "anomaly detection",
  getAnomalyWidgetSummary: "anomaly summary",
  getAccountability: "accountability",
  getWaterLoss: "supply and demand meters",
  getRevenueLoss: "leak revenue estimate",
  getLeakMaintenance: "leak maintenance log",
  getWaterUsageBreakdown: "water usage",
  generateWaterUsageReportFile: "water usage report",
  getAllYearConsumption: "consumption history",
  getFlowPredictions: "flow predictions",
  getOutliersCountNew: "flow outliers",
  getOutliersNew: "flow outliers",
  getOutliersByMeterNew: "flow outliers",
  getMeterLocations: "meter locations",
  getMeterOperationalCount: "meter status",
  getMeterMetadataDirectory: "meter directory",
  getMeterLogs: "meter logs",
  getWaterRate: "water rates",
};
// Accountability and water loss for one system name it.
const SYSTEMS = { plant: "potable", potable: "potable", non_potable: "non-potable" };
// The period a tool reads when the assistant names none (tools/functions.py).
const DEFAULT_RANGE = {
  getWaterLeak: "Past Month",
  getAccountability: "Past Month",
  getWaterLoss: "Past Month",
  getRevenueLoss: "Past Month",
  getWaterUsageBreakdown: "Past Month",
  getOutliersCountNew: "Past Month",
  getOutliersNew: "Past Month",
  getOutliersByMeterNew: "Past Month",
  getAnomalyMeters: "last_30_days",
  getAnomalyWidgetSummary: "last_30_days",
};
const RANGES = {
  past_day: "past day",
  last_7_days: "last 7 days",
  last_30_days: "last 30 days",
  last_90_days: "last 90 days",
  last_6_months: "last 6 months",
  last_year: "last year",
};

const dayOf = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return match ? new Date(Date.UTC(+match[1], +match[2] - 1, +match[3])) : null;
};
const formatDay = (date, options) => date.toLocaleDateString("en-US", { timeZone: "UTC", ...options });
const FULL = { month: "short", day: "numeric", year: "numeric" };
const SHORT = { month: "short", day: "numeric" };

// A custom range: "Oct 1 – 24, 2026", "Sep 27 – Oct 27, 2026", "Oct 10, 2026".
function dateRange(start, end) {
  const from = dayOf(start);
  const to = dayOf(end);
  if (from && to) {
    if (+from === +to) return formatDay(from, FULL);
    if (from.getUTCFullYear() !== to.getUTCFullYear()) return `${formatDay(from, FULL)} – ${formatDay(to, FULL)}`;
    if (from.getUTCMonth() !== to.getUTCMonth()) return `${formatDay(from, SHORT)} – ${formatDay(to, FULL)}`;
    return `${formatDay(from, SHORT)} – ${to.getUTCDate()}, ${to.getUTCFullYear()}`;
  }
  if (from) return `since ${formatDay(from, FULL)}`;
  if (to) return `to ${formatDay(to, FULL)}`;
  return null;
}

// The period a tool's data covers, as the Source line says it.
function periodOf(name, args) {
  if (args.start_date || args.end_date) return dateRange(args.start_date, args.end_date);
  if (name === "getFlowPredictions") return args.use_next ? "next week" : "this week";
  // getWaterLeak's period is its interval (or option); the anomaly tools'
  // their time_range; the others' their option - their interval is the
  // data's step (Days, Hours).
  const range =
    (name === "getWaterLeak" ? args.option || args.interval : name.startsWith("getAnomaly") ? args.time_range : args.option) ||
    DEFAULT_RANGE[name];
  if (typeof range !== "string" || !range.trim() || /^custom$/i.test(range.trim())) return null;
  // The leak data's Past Day is the last completed day (alertCounts.js).
  if (name === "getWaterLeak" && /^past day$/i.test(range.trim())) return "last full day";
  return RANGES[range.trim()] || range.trim().replace(/_/g, " ").toLowerCase();
}

const toolsOf = (tools) => (Array.isArray(tools) ? tools : []).filter((tool) => tool && typeof tool.name === "string");
const argsOf = (tool) => (tool.arguments && typeof tool.arguments === "object" ? tool.arguments : {});

/** The answer's Source line - each thing its data came from, and for when:
 * "leak meters · last full day". An investigation's: the data it checked, and
 * the period - "leak records, daily usage, system health · last 30 days".
 * Empty when it read no data. */
export function answerSource(tools, investigation) {
  const checked = Array.isArray(investigation?.sources) ? investigation.sources.filter((s) => typeof s === "string" && s) : [];
  if (checked.length) return investigation.period ? `${checked.join(", ")} · ${investigation.period}` : checked.join(", ");
  const sources = new Map();
  for (const tool of toolsOf(tools)) {
    const label = SOURCES[tool.name];
    if (!label) continue;
    const args = argsOf(tool);
    const system = (tool.name === "getAccountability" || tool.name === "getWaterLoss") && SYSTEMS[args.system];
    const source = system ? `${system} ${label}` : label;
    const periods = sources.get(source) || [];
    const period = periodOf(tool.name, args);
    if (period && !periods.includes(period)) periods.push(period);
    sources.set(source, periods);
  }
  return [...sources].map(([source, periods]) => (periods.length ? `${source} · ${periods.join(" and ")}` : source)).join("; ");
}

// ── View on page: deep links ────────────────────────────────────────────────
// The page with more on what an answer is about, opened as its data was read:
// the tool's period as the page's window (range, or from/to), its one meter
// selected, its category and system, on the district the answer is of - a
// navigation ({ page, label, params }, by the Modules menu's page keys,
// mw2/context.js) the chat window opens as it opens a page asked for
// (chatNavigation.js navigationTarget: the "From MizuIQ" chip and Back).
// Digital Twin's Alert Center, which takes no context, is a New Dashboard
// tab instead (dashboardTab, as Mizu AI's Live Status opens it).
const ALERT_CENTER = { kind: "tab", label: "Open Alert Center", dashboardTab: "twin" };
const TOOL_LINKS = {
  getWaterLeak: "leak-flow",
  getRevenueLoss: "leak-flow",
  getLeakMaintenance: "leak-flow",
  getAnomalyMeters: "anomaly",
  getAnomalyWidgetSummary: "anomaly",
  getFlowPredictions: "predictive",
  getWaterUsageBreakdown: "water-usage",
  generateWaterUsageReportFile: "water-usage",
  getAllYearConsumption: "water-usage",
  getOutliersCountNew: "outliers",
  getOutliersNew: "outliers",
  getOutliersByMeterNew: "outliers",
  getAccountability: "flow-analytics",
  getWaterLoss: "flow-analytics",
};
// An investigation's page, by its issue.
const ISSUE_LINKS = {
  leak: "leak-flow",
  anomaly: "anomaly",
  backflow: "backflow",
  no_flow: "no-recent-flow",
  meter_health: "system-health",
  accountability: "flow-analytics",
};
// What each page reads from its URL besides the district (the pages' own
// URL handling); the rest of a tool's context is left out.
const PAGE_PARAMS = {
  "leak-flow": ["range", "from", "to", "meter", "category"],
  anomaly: ["range", "from", "to", "meter", "category"],
  backflow: ["meter", "category"],
  "no-recent-flow": ["meter", "category"],
  "water-usage": ["range", "from", "to", "meter"],
  "flow-analytics": ["range", "from", "to", "system", "view"],
};
// The pages' window presets (mw2/ranges.js), by the periods' names the tools
// take - "Past Week" (leak, accountability, usage), "last_7_days" (anomaly,
// investigation) - and their words.
const RANGE_KEYS = {
  "past day": "D", day: "D", "1 day": "D", "24 hours": "D", "past 24 hours": "D", "last full day": "D",
  "past week": "W", "last 7 days": "W", "past 7 days": "W", "last week": "W", week: "W", "7 days": "W",
  "past month": "1M", "last 30 days": "1M", "past 30 days": "1M", "last month": "1M", month: "1M", "30 days": "1M",
  "past 3 months": "3M", "last 90 days": "3M", "past 90 days": "3M", "last 3 months": "3M", "3 months": "3M", quarter: "3M",
  "past 6 months": "6M", "last 6 months": "6M", "past 180 days": "6M", "6 months": "6M",
  "past year": "1Y", "last year": "1Y", year: "1Y", "1 year": "1Y", "12 months": "1Y", "past 12 months": "1Y",
  "year to date": "YTD", ytd: "YTD",
};
// The period an investigation reads when none is named (python
// tools/investigation.py): one meter's, the last 30 days; the system's, by
// issue.
const ISSUE_DEFAULT_RANGE = { leak: "D", anomaly: "1M", no_flow: "1M", backflow: "1M", meter_health: "1M", accountability: "1M", overview: "W" };
// Flow Analytics' systems (TotalFlow.jsx), by the tools' names for them.
const FLOW_SYSTEMS = new Set(["total", "plant", "potable", "non_potable"]);

const isoDay = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value || "").slice(0, 10)) ? String(value).slice(0, 10) : null);
const shiftDay = (iso, days) => {
  const date = dayOf(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};
const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

// The window a tool read, as a page's URL says it: { from, to } for dates (a
// missing end is today, a missing start 30 days before it - as the tools
// read them), { range } for a named period, else {} (the page's default).
function windowParams(start, end, named) {
  const from = isoDay(start);
  const to = isoDay(end);
  if (from || to) {
    const last = to || localToday();
    const first = from || shiftDay(last, -30);
    return first <= last ? { from: first, to: last } : { from: last, to: first };
  }
  const key = typeof named === "string" ? RANGE_KEYS[named.trim().toLowerCase().replace(/[\s_-]+/g, " ")] : null;
  return key ? { range: key } : {};
}

// One meter's ID from a tool's meter_id ("126171", not a list) or meter_ids.
function oneMeter(value) {
  const ids = (Array.isArray(value) ? value : String(value ?? "").split(",")).map((id) => String(id ?? "").trim()).filter(Boolean);
  return ids.length === 1 ? ids[0] : null;
}
const categoryOf = (value) => (typeof value === "string" && value.trim() && !/^all$/i.test(value.trim()) ? value.trim() : null);

// A data tool's context, by the page's names: its window, meter, category
// and system.
function toolContext(name, args) {
  const period = name === "getWaterLeak" ? args.option || args.interval : name.startsWith("getAnomaly") ? args.time_range : args.option;
  const windowed = ["getWaterLeak", "getRevenueLoss", "getAnomalyMeters", "getAnomalyWidgetSummary", "getAccountability", "getWaterLoss",
    "getWaterUsageBreakdown", "generateWaterUsageReportFile"].includes(name);
  const context = windowed ? windowParams(args.start_date, args.end_date, period || DEFAULT_RANGE[name]) : {};
  context.meter = oneMeter(args.meter_id ?? args.meter_ids);
  context.category = categoryOf(args.category);
  if (name === "getAccountability" || name === "getWaterLoss") {
    context.system = FLOW_SYSTEMS.has(args.system) ? args.system : null;
    context.view = "accountability";
  }
  return context;
}

// An investigation's context, from its tool's arguments (investigateIssue).
function issueContext(issue, args) {
  const meter = oneMeter(args.meter_id);
  const context = windowParams(args.start_date, args.end_date, args.time_range);
  if (!context.range && !context.from) context.range = meter ? "1M" : ISSUE_DEFAULT_RANGE[issue] || "1M";
  context.meter = meter;
  context.category = meter ? null : categoryOf(args.category);
  if (issue === "accountability") {
    context.system = FLOW_SYSTEMS.has(args.system) ? args.system : null;
    context.view = "accountability";
  }
  return context;
}

// The link to a page with a context: { kind: "link", key, label, navigation }
// - the context only as far as the page reads it, and the district.
function pageLink(key, context, district) {
  const page = PAGES[key];
  if (!page) return null;
  const params = {};
  (PAGE_PARAMS[key] || []).forEach((name) => {
    if (context[name]) params[name] = context[name];
  });
  if (typeof district === "string" && district.trim()) params.district = district.trim();
  return { kind: "link", key: `link-${key}`, label: `View on ${page.label}`, navigation: { page: key, label: page.label, params } };
}

const MAX_LINKS = 2;

/** An answer's "View on <page>" links - each page once, the first tool's
 * context for it, at most MAX_LINKS - or for an investigation its issue's
 * page; the Alert Center for meter health and an overview (a dashboard tab:
 * { kind: "tab" }). The answer's one meter is selected on a page that lists
 * meters when its tool named none. */
export function answerLinks(tools, meterIds, investigation, district) {
  const ids = uniqueIds(meterIds);
  const selected = ids.length === 1 ? ids[0] : null;
  if (investigation && typeof investigation === "object") {
    const key = ISSUE_LINKS[investigation.issue];
    if (!key) return investigation.issue ? [ALERT_CENTER] : [];
    const tool = toolsOf(tools).find((t) => t.name === "investigateIssue");
    const context = issueContext(investigation.issue, tool ? argsOf(tool) : {});
    context.meter = context.meter || selected;
    const link = pageLink(key, context, district);
    return link ? [link] : [];
  }
  const links = [];
  for (const tool of toolsOf(tools)) {
    if (tool.name === "getMeterHealth") {
      if (!links.includes(ALERT_CENTER)) links.push(ALERT_CENTER);
      continue;
    }
    const key = TOOL_LINKS[tool.name];
    if (!key || links.some((link) => link.key === `link-${key}`)) continue;
    const context = toolContext(tool.name, argsOf(tool));
    context.meter = context.meter || selected;
    const link = pageLink(key, context, district);
    if (link) links.push(link);
  }
  return links.slice(0, MAX_LINKS);
}
// An answer's meters on the dashboard's map: the alert overlay switched on
// with them, what the map's chip calls them (one, several), and the page
// its button narrows to them (mizuAi.js DIAGNOSIS_SOURCES) - by the tool
// that named them (python agentic_ai/chatbot_core.py METER_TOOLS).
const LOCATE = {
  getWaterLeak: { alertType: "leak", names: ["leaking meter", "leaking meters"], source: "leak" },
  getLeakMaintenance: { alertType: "leak", names: ["meter with a leak report", "meters with leak reports"], source: "leak" },
  getAnomalyMeters: { alertType: "anomaly", names: ["meter with an anomaly", "meters with anomalies"], source: "anomaly" },
  getMeterHealth: { names: ["flagged meter", "flagged meters"], source: "health" },
  getOutliersNew: { names: ["meter with outliers", "meters with outliers"] },
  getOutliersByMeterNew: { names: ["meter with outliers", "meters with outliers"] },
  getMeterLocations: { names: ["meter", "meters"] },
  // A Super Mode query's rows, when they list meters (their meter_id).
  runSqlQuery: { names: ["meter in the result", "meters in the result"] },
};

// An investigation's issue: the source page its meters' "Locate on map"
// narrows to (mizuAi.js DIAGNOSIS_SOURCES), and the map's alert overlays
// (the dashboard's).
const ISSUE_SOURCES = { leak: "leak", anomaly: "anomaly", no_flow: "noflow", meter_health: "health" };
const ALERT_TYPES = new Set(["leak", "anomaly", "backflow", "noflow"]);

// "Investigate root cause" under an answer about an issue: the question it
// asks in Investigation mode - about the answer's one meter, or the issue
// across the system for the same period. The lists only with meters in them.
const INVESTIGATE = {
  getWaterLeak: { list: true, one: (id) => `Why is meter ${id} leaking?`, many: (when) => `What is causing the leaks${when}?` },
  getAnomalyMeters: { list: true, one: (id) => `What caused the anomaly at meter ${id}?`, many: (when) => `What is behind the anomalies${when}?` },
  getAnomalyWidgetSummary: { many: (when) => `What is behind the anomalies${when}?` },
  getMeterHealth: { list: true, one: (id) => `What is wrong with meter ${id}?`, many: () => "What is behind the active alerts?" },
  getAccountability: { many: (when) => `What is affecting accountability${when}?` },
  getWaterLoss: { many: (when) => `What is causing the water loss${when}?` },
};

// " in the past month", " for Oct 1 – 24, 2026" - "" for the latest data.
function periodPhrase(name, args) {
  const period = periodOf(name, args);
  if (!period || period === "last full day" || /^(this|next) week$/.test(period)) return "";
  return /^(past|last)\b/.test(period) ? ` in the ${period}` : ` for ${period}`;
}

const uniqueIds = (meterIds) =>
  [...new Set((Array.isArray(meterIds) ? meterIds : []).map((id) => String(id ?? "").trim()).filter(Boolean))];

/** The question an answer's "Investigate root cause" asks, or null when it is
 * about nothing to investigate. */
export function investigateQuestion(tools, meterIds) {
  const tool = toolsOf(tools).find((t) => INVESTIGATE[t.name]);
  if (!tool) return null;
  const ask = INVESTIGATE[tool.name];
  const ids = uniqueIds(meterIds);
  if (ask.list && !ids.length) return null;
  if (ids.length === 1 && ask.one) return ask.one(ids[0]);
  return ask.many(periodPhrase(tool.name, argsOf(tool)));
}

/** The answer's buttons: "Locate on map" for the meters it is about, "View on
 * <page>" for the pages with more on it (answerLinks - on `district`, the
 * answer's) and, for an answer about an issue, "Investigate root cause" -
 * each { kind: "locate" | "tab" | "link" | "investigate", label, ... }. An
 * investigation's: its meters on the map (its issue's overlay) and its page. */
export function answerActions(tools, meterIds, investigation, district) {
  const names = toolsOf(tools).map((tool) => tool.name);
  const ids = uniqueIds(meterIds);
  const actions = [];
  if (investigation && typeof investigation === "object") {
    if (ids.length) {
      actions.push({
        kind: "locate",
        label: "Locate on map",
        meterIds: ids,
        alertType: ALERT_TYPES.has(investigation.alert_type) ? investigation.alert_type : null,
        setLabel: ids.length === 1 ? "investigated meter" : "meters in the investigation",
        source: ISSUE_SOURCES[investigation.issue] || null,
      });
    }
    return [...actions, ...answerLinks(tools, ids, investigation, district)];
  }
  const locate = names.map((name) => LOCATE[name]).find(Boolean);
  if (locate && ids.length) {
    actions.push({
      kind: "locate",
      label: "Locate on map",
      meterIds: ids,
      alertType: locate.alertType || null,
      setLabel: locate.names[ids.length === 1 ? 0 : 1],
      source: locate.source || null,
    });
  }
  actions.push(...answerLinks(tools, ids, null, district));
  const question = investigateQuestion(tools, ids);
  if (question) actions.push({ kind: "investigate", label: "Investigate root cause", question });
  return actions;
}

const listOf = (items) => (items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}` : items[0] || "");

/** What an answer still on its way is doing, as its status line says it
 * (the stream's "status": thinking, the tools reading their data, writing -
 * and in Super Mode the query written, checked and run). */
export function statusText(status, investigating = false, superMode = false) {
  if (status?.stage === "sql") return "Writing the SQL query…";
  if (status?.stage === "checking") return "Checking the query against the database…";
  if (status?.stage === "running") return "Running the query, read-only…";
  if (status?.stage === "tools") {
    const names = Array.isArray(status.tools) ? status.tools : [];
    if (names.includes("investigateIssue")) {
      return "Investigating: checking leak records, daily use, the anomaly model, meter health, nearby meters and maintenance logs…";
    }
    // Only a page to open (python agentic_ai/navigation.py).
    if (names.length && names.every((name) => name === "openPage")) return "Opening the page…";
    const read = [...new Set(names.map((name) => SOURCES[name]).filter(Boolean))];
    return read.length ? `Reading ${listOf(read)}…` : "Reading the data…";
  }
  if (status?.stage === "writing") return superMode ? "Writing the answer from the results…" : "Writing the answer…";
  return investigating ? "Starting the investigation…" : "Thinking…";
}

/** Whether a suggested question is an investigation - asked in Investigation
 * mode whichever mode the window is in. */
export const isInvestigation = (question) =>
  /^\s*(investigate|why\b|what (caused|is (causing|behind)|is wrong))/i.test(String(question || ""));
